import { Popup } from 'react-map-gl/maplibre';
import { Separator } from 'shared-ui';

import { formatNetFunc, formatPipeLength } from '../utils/pipelineFeatureProperties';
import type { PipelineFeatureProperties } from '../utils/pipelineFeatureProperties';

interface PipelinePopupProps {
    pipeline: PipelineFeatureProperties;
    /** Geographic anchor — MapLibre projects + tracks this itself, so no screen-pixel math here. */
    longitude: number;
    latitude: number;
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-foreground text-sm leading-5 font-medium">{label}</span>
            <span className="text-muted-foreground truncate text-right text-sm leading-5 font-normal">{value}</span>
        </div>
    );
}

/**
 * Pipeline hover card, positioned by MapLibre's own `<Popup>`: it projects the lng/lat, tracks
 * it on pan/zoom, picks a side that fits the viewport (`anchor` unset = auto), and clears the
 * cursor anchor via `offset`. Because MapLibre positions by the rendered DOM box, the card width
 * can be set in rem with no pixel math. The default popup chrome (tip, padding, background) is
 * stripped via the `.orbit-hover-popup` overrides in MapView.css; `--passive` keeps this
 * hover-only card from ever capturing the cursor.
 */
export function PipelinePopup({ pipeline, longitude, latitude }: PipelinePopupProps) {
    const length = formatPipeLength(pipeline.length);

    return (
        <Popup
            longitude={longitude}
            latitude={latitude}
            closeButton={false}
            closeOnClick={false}
            closeOnMove={false}
            focusAfterOpen={false}
            offset={12}
            maxWidth="none"
            className="orbit-hover-popup orbit-hover-popup--passive"
        >
            <div className="border-border/30 bg-card pointer-events-none w-[15rem] rounded-lg border p-4 shadow-lg">
                <p className="text-foreground text-base leading-tight font-black uppercase">Pipeline</p>

                <Separator className="my-2.5" />

                <div className="space-y-3">
                    <FieldRow label="Pipe ID" value={pipeline.id || '—'} />
                    <FieldRow label="Pipe Type" value={formatNetFunc(pipeline.net_func)} />
                    <FieldRow label="Length" value={length ?? '—'} />
                </div>
            </div>
        </Popup>
    );
}
